import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useWordSuggestions } from '@/hooks/useWordSuggestions';
import { toast } from 'sonner';
import { Check, X, MessageSquare, Clock, User } from 'lucide-react';

export const WordSuggestionsAdmin = () => {
  const { pendingSuggestions, fetchPendingSuggestions, reviewSuggestion, loading } = useWordSuggestions();
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [editedClues, setEditedClues] = useState<Record<number, string>>({});

  useEffect(() => {
    fetchPendingSuggestions();
  }, []);

  const handleReview = async (id: number, action: 'approve' | 'reject') => {
    setProcessingId(id);
    const clueToUse = editedClues[id] !== undefined ? editedClues[id] : pendingSuggestions.find(s => s.id === id)?.clue || '';
    const { error } = await reviewSuggestion(id, action, clueToUse);
    setProcessingId(null);

    if (error) {
      toast.error(error);
    } else {
      toast.success(action === 'approve' ? 'Ordet godkändes!' : 'Förslaget avslogs');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && pendingSuggestions.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Laddar...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Ordförslag att granska
        </CardTitle>
        <CardDescription>
          Granska och godkänn användarnas ordförslag
        </CardDescription>
      </CardHeader>
      <CardContent>
        {pendingSuggestions.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">
            Inga väntande förslag
          </p>
        ) : (
          <div className="space-y-4">
            {pendingSuggestions.map((suggestion) => (
              <div 
                key={suggestion.id} 
                className="border rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-lg font-bold text-primary">
                        {suggestion.word}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {suggestion.word.length} bokstäver
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`clue-${suggestion.id}`} className="text-sm font-medium">
                        Ledtråd
                      </Label>
                      <Textarea
                        id={`clue-${suggestion.id}`}
                        value={editedClues[suggestion.id] ?? suggestion.clue ?? ''}
                        onChange={(e) => setEditedClues(prev => ({ ...prev, [suggestion.id]: e.target.value }))}
                        className="text-sm min-h-[60px]"
                        placeholder="Ange ledtråd..."
                      />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {suggestion.submitted_by}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(suggestion.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleReview(suggestion.id, 'approve')}
                      disabled={processingId === suggestion.id}
                      className="flex-1"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Godkänn
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleReview(suggestion.id, 'reject')}
                      disabled={processingId === suggestion.id}
                      className="flex-1"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Avslå
                    </Button>
                  </div>
                  </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
